import { isNil } from '@fmnts/core';
import {
  combineLatest,
  map,
  MonoTypeOperatorFunction,
  Observable,
  switchMap,
} from 'rxjs';
import { TemplatePart } from './template.model';

/**
 * RegExp for matching instances of an expression
 * in a string, which takes the form of
 * `{{ some-expression }}`
 */
export const expressionRegex = /{{\s*(.*?)\s*}}/;
const globalExpressionRegex = new RegExp(expressionRegex, 'g');

/**
 * Splits the given `input` into an ordered array of `TemplatePart`s.
 *
 * @param input Input string
 *
 * @returns
 * Array of parts that represent the `input`.
 *
 * @example
 * // For some templated string
 * const parts = splitTemplate('hello {{first_name}} {{ last_name }}');
 * parts.length === 4;
 * // Has these literals
 * parts[0].raw === 'hello ';
 * parts[2].raw === ' ';
 * // and these expressions
 * parts[1].expression === 'first_name';
 * parts[3].expression === 'last_name';
 *
 *
 * @example
 * // For an empty literal
 * const parts = splitTemplate('');
 * parts.length === 1
 * parts[0].raw === ''
 */
export function splitTemplate(input: string): TemplatePart[] {
  const parts: TemplatePart[] = [];
  /** Index of the last found expression */
  let lastFoundIndex = 0;

  // if there is no input, return empty `TemplatePart`
  if (isNil(input)) {
    return parts;
  }

  // Perform the search for any templated expression on
  // the input. A `RegExp` with global flag becomes stateful
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp/exec
  for (
    let match = globalExpressionRegex.exec(input);
    match !== null;
    match = globalExpressionRegex.exec(input)
  ) {
    const [raw, expression] = match;
    const start = match.index;
    const end = start + raw.length;

    // Check if there is some more string between current and last match
    if (start > lastFoundIndex) {
      parts.push({
        type: 'literal',
        raw: input.substring(lastFoundIndex, start),
        start: lastFoundIndex,
        end: start,
      });
    }

    // Now add new match
    parts.push({ type: 'expression', raw, expression, start, end });

    lastFoundIndex = end;
  }

  // Add rest of string (could be everything)
  if (lastFoundIndex !== input.length || parts.length === 0) {
    parts.push({
      type: 'literal',
      raw: input.substring(lastFoundIndex),
      start: lastFoundIndex,
      end: input.length,
    });
  }

  return parts;
}

/**
 * Splits an emitted value into its template parts and applies a
 * given `selector` to each, to turn them into an Observable.
 * Those observables are combined into a single stream and rejoined
 * once all have emitted a value.
 *
 * @param selector
 * Function that is applied for each `part` of the templated string,
 * to turn that part into an `Observable`.
 *
 * @returns
 *
 * @example
 * // Given that translate$ returns an Observable for the
 * // passed string that emits with the translated value
 * of('{{hello}} JavaScript').pipe(
 *  interpolateTemplate((part) =>
 *    part.type === 'literal'
 *      // Emit literals literally
 *      ? of(part.raw)
 *      // Emit expressions as translated strings
 *      : translate$(part.expression)
 *  )
 * );
 * // -> 'hello JavaScript'
 * // -> 'bonjour JavaScript'
 */
export function interpolateTemplate(
  selector: (
    part: TemplatePart,
    index: number,
    array: TemplatePart[],
  ) => Observable<string>,
): MonoTypeOperatorFunction<string> {
  return (s$) =>
    s$.pipe(
      map(splitTemplate),
      switchMap((parts) => combineLatest(parts.map(selector))),
      map((parts) => parts.join('')),
    );
}
